import React, { useCallback, useRef, useEffect, useState } from "react";
import { Box, Button, getColor, Icons, Modal, Typography, useToast } from "fds";
import "./index.scss";
import classNames from "classnames";
import { Workgroup, WorkgroupDetailDTO } from "../../types/workgroup";
import WorkgroupInfoTab from "./WorkgroupInfoTab";
import WorkgroupInfoSettingTab from "./WorkgroupInfoSettingTab";
import WorkgroupAuthSettingTab from "./WorkgroupAuthSettingTab";
import WorkgroupMemberTab from "./WorkgroupMemberTab";
import WorkgroupAlarmTab from "./WorkgroupAlarmTab";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { nanoid } from "@reduxjs/toolkit";
import { userInfo } from "os";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import { t } from "i18next";
import useAdminWorkgroupStore from "../../redux/dispatcher/useAdminWorkgroupStore";

interface WorkgroupInfoModalProps {
  workgroupInfo?: Workgroup;
  isWorkgroupManager: boolean;
  startTab?: string;
  open: boolean;
  fromAdmin?: boolean;
  workgroupSettingInfo?: {
    x: number;
    y: number;
    id: string;
    workgroupInfo?: Workgroup;
  };
  setWorkgroupSettingInfo?: (req: {
    x: number;
    y: number;
    id: string;
    workgroupInfo?: Workgroup;
  }) => void;
  onClose: () => void;
  policy?: WorkgroupDetailDTO;
}
const WorkgroupInfoModal: React.FC<WorkgroupInfoModalProps> = ({
  workgroupInfo,
  open,
  startTab,
  fromAdmin = false,
  isWorkgroupManager,
  workgroupSettingInfo,
  policy,
  setWorkgroupSettingInfo,
  onClose,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  const tabs = [
    {
      id: "info",
      name: t("common.basicInfo"),
      icon: <Icons variant="fill" label="detail" />,
    },
    {
      id: "setting",
      name: t("common.general"),
      icon: <Icons variant="fill" label="setting" />,
    },
    {
      id: "auth",
      name: t("workgroup.workgroupPermssion"),
      icon: <Icons variant="fill" label="shield" />,
    },
    {
      id: "members",
      name: t("common.member"),
      icon: <Icons variant="fill" label="users" />,
    },
    {
      id: "alarm",
      name: t("header.alarm.title"),
      icon: <Icons variant="fill" label="notification" />,
    },
  ];
  const toast = useToast();
  const { workgroupState, getWorkgroupDetail, updateWorkgroupInfo } =
    useWorkgroupStore();
  const { adminWorkgroupStore, getAdminWorkgroupDetail } =
    useAdminWorkgroupStore();
  const [tab, changeTab] = useState(
    startTab && tabs.filter((item) => item.id === startTab).length > 0
      ? startTab
      : "info"
  );
  const [workgroupDetail, changeWorkgroupDetail] =
    useState<WorkgroupDetailDTO>();
  const [tempWorkgroupInfo, changeTempWorkgroupInfo] = useState<
    Workgroup | undefined
  >(workgroupInfo);
  const [tempWorkgroupPolicy, changeTempWorkgroupPolicy] = useState(
    workgroupDetail?.policy
  );
  useEffect(() => {
    if (workgroupInfo && !fromAdmin) {
      getWorkgroupDetail({ workgroupId: workgroupInfo?.workgroupId });
    } else if (workgroupInfo && fromAdmin) {
      getAdminWorkgroupDetail(workgroupInfo?.workgroupId);
    }
  }, [workgroupInfo?.workgroupId]);
  useEffect(() => {
    const policy =
      workgroupState.workgroupDetail ?? adminWorkgroupStore.workgroupDetail;
    const newPolicy = {
      ...policy?.policy,
      policy: {
        ...policy?.policy?.policy,
        allowDays: policy?.policy?.policy?.allowDays - 1,
        fileShareLinkPolicy: {
          ...policy?.policy?.policy?.fileShareLinkPolicy,
          allowDays: policy?.policy?.policy?.fileShareLinkPolicy?.allowDays - 1,
        },
      },
    };
    changeTempWorkgroupInfo(policy?.workgroup);
    changeWorkgroupDetail({
      ...policy,
      policy: newPolicy,
    });
    changeTempWorkgroupPolicy(newPolicy);
  }, [
    workgroupState.workgroupDetail,
    adminWorkgroupStore.workgroupDetail,
    tab,
  ]);
  const checkIsDisabled = () => {
    if (tab === "info")
      return (
        !tempWorkgroupInfo?.workgroupName ||
        tempWorkgroupInfo?.workgroupName.trim().length === 0 ||
        (workgroupInfo?.workgroupName === tempWorkgroupInfo.workgroupName &&
          workgroupInfo.desc === tempWorkgroupInfo.desc)
      );
    if (tab === "setting") {
      return (
        (tempWorkgroupInfo?.invitePolicy === workgroupInfo?.invitePolicy &&
          tempWorkgroupInfo?.endDate === workgroupInfo?.endDate &&
          JSON.stringify(tempWorkgroupPolicy?.policy?.fileShareLinkPolicy) ===
            JSON.stringify(
              workgroupDetail?.policy?.policy?.fileShareLinkPolicy
            )) ||
        tempWorkgroupPolicy?.policy?.fileShareLinkPolicy?.allowDays + "" === ""
      );
    } else
      return (
        JSON.stringify(tempWorkgroupPolicy?.policy) ===
        JSON.stringify(workgroupDetail?.policy?.policy)
      );
  };
  const handleSubmitWorkgroupInfo = async () => {
    if (!workgroupInfo) return;
    let newWorkgroupInfo =
      tempWorkgroupInfo &&
      JSON.stringify({
        ...tempWorkgroupInfo,
        workgroupName: tempWorkgroupInfo.workgroupName.trim(),
      }) !== JSON.stringify(workgroupInfo)
        ? tempWorkgroupInfo
        : undefined;
    if (newWorkgroupInfo && setWorkgroupSettingInfo && workgroupSettingInfo) {
      setWorkgroupSettingInfo({
        ...workgroupSettingInfo,
        workgroupInfo: newWorkgroupInfo,
      });
    }
    let resp: any = await updateWorkgroupInfo({
      workgroupId: workgroupInfo?.workgroupId,
      workgroup:
        tempWorkgroupInfo &&
        JSON.stringify({
          ...tempWorkgroupInfo,
          workgroupName: tempWorkgroupInfo.workgroupName.trim(),
        }) !== JSON.stringify(workgroupInfo)
          ? tempWorkgroupInfo
          : undefined,
      policy:
        tempWorkgroupPolicy &&
        JSON.stringify(tempWorkgroupPolicy) !==
          JSON.stringify(workgroupDetail?.policy)
          ? {
              ...tempWorkgroupPolicy,
              policy: tempWorkgroupPolicy.policy
                ? {
                    ...tempWorkgroupPolicy.policy,
                    allowDays: (tempWorkgroupPolicy?.policy.allowDays ?? 0) + 1,
                    fileShareLinkPolicy: tempWorkgroupPolicy.policy
                      .fileShareLinkPolicy
                      ? {
                          ...tempWorkgroupPolicy.policy.fileShareLinkPolicy,
                          allowDays:
                            tempWorkgroupPolicy?.policy?.fileShareLinkPolicy
                              ?.allowDays + 1,
                        }
                      : undefined,
                  }
                : undefined,
              changeCode: "UPDATE",
              targetType: "WORKGROUP",
              updateUser: loginDto.userInfo.userId ?? "",
            }
          : undefined,
    });

    if (resp.payload) {
      toast.toastMsg(nanoid(), t("common.alert.editSuccess"), "success");
    } else if (resp.error) {
      toast.toastMsg(
        nanoid(),
        resp.error.message ?? t("common.alert.fail"),
        "error"
      );
    }
  };
  const renderSideMenu = () => {
    return (
      <Box direction="column" classes="profileModal-sidemenu">
        {tabs
          .filter((item, index) => !fromAdmin || index < 4)
          .map((item) => (
            <Box
              classes={classNames("sidemenu-button", {
                selected: tab.includes(item.id),
              })}
              onClick={() => changeTab(item.id)}
            >
              {item.icon}
              <Typography
                classes="mg ml-12"
                type="body2_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {item.name}
              </Typography>
            </Box>
          ))}
      </Box>
    );
  };
  const renderBody = () => {
    return tab === "info" ? (
      <WorkgroupInfoTab
        workgroupInfo={tempWorkgroupInfo}
        changeWorkgroupInfo={changeTempWorkgroupInfo}
        isWorkgroupManager={isWorkgroupManager}
        onClose={onClose}
      />
    ) : tab === "setting" ? (
      <WorkgroupInfoSettingTab
        workgroupInfo={tempWorkgroupInfo}
        changeWorkgroupInfo={changeTempWorkgroupInfo}
        policy={tempWorkgroupPolicy}
        changeWorkgroupPolicy={changeTempWorkgroupPolicy}
        isWorkgroupManager={isWorkgroupManager}
      />
    ) : tab === "auth" ? (
      <WorkgroupAuthSettingTab
        policy={tempWorkgroupPolicy}
        isWorkgroupManager={isWorkgroupManager}
        changeWorkgroupPolicy={changeTempWorkgroupPolicy}
      />
    ) : tab === "members" ? (
      <WorkgroupMemberTab
        workgroupInfo={workgroupDetail?.workgroup}
        isWorkgroupManager={isWorkgroupManager}
      />
    ) : (
      <WorkgroupAlarmTab
        workgroupInfo={workgroupDetail?.workgroup}
        policy={tempWorkgroupPolicy}
        changeWorkgroupPolicy={changeTempWorkgroupPolicy}
        isWorkgroupManager={isWorkgroupManager}
      />
    );
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isWorkgroupManager ? t("workgroup.setting") : t("workgroup.info")}
      width={816}
      bodyMargin={0}
      classes="workgroupModal"
      bodyPadding={0}
      height={712}
      bodyHeight={isWorkgroupManager && tab !== "members" ? 554 : 624}
      isFooter={isWorkgroupManager && tab !== "members"}
      footer={
        <Box>
          <Button
            label={t("common.cancel")}
            btnStyle="fill"
            type="secondary"
            onClick={onClose}
          />
          <Button
            label={t("common.save")}
            btnStyle="fill"
            state={checkIsDisabled() ? "disable" : "default"}
            onClick={handleSubmitWorkgroupInfo}
          />
        </Box>
      }
      body={
        <Box
          width="100%"
          height={"100%"}
          maxHeight={isWorkgroupManager && tab !== "members" ? 554 : 624}
        >
          {renderSideMenu()}
          <Box classes="workgroupModal-body">{renderBody()}</Box>
        </Box>
      }
    />
  );
};
export default WorkgroupInfoModal;
